import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { QueueCycleTime } from '@App/models';
import { SharedModule } from '@Shared/shared.module';
import { ChartModule, UIChart } from 'primeng/chart';

const CHART_COLORS = [
    '#36A2EB', '#FF6384', '#FFCE56', '#4BC0C0', '#9966FF', 
    '#FF9F40', '#7BC225', '#B325C2', '#C25B25'
];

@Component({
    selector: 'case-cycle-time-per-queue-line-chart',
    templateUrl: './case-cycle-time-per-queue-line-chart.component.html',
    styleUrl: './case-cycle-time-per-queue-line-chart.component.scss',
    imports: [SharedModule, ChartModule]
})
export class CaseCycleTimePerQueueLineChartComponent implements OnChanges {
    @ViewChild('chart') chart: UIChart;

    @Input() isExpanded = false;
    @Input() queueCycleTimes: QueueCycleTime[] = [];

    chartData: any = {
        labels: [],
        datasets: []
    };
    tooltipLabels: string[] = [];

    chartOptions = {
        animation: {
            duration: 0
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'nearest',
                intersect: false,
                axis: 'xy',
                usePointStyle: true,
                caretPadding: 15,
                position: 'nearest',
                yAlign: (context: any) => {
                    if (!context.tooltip?.dataPoints?.[0]) return 'bottom';

                    const value = context.tooltip.dataPoints[0].raw;
                    const scale = context.chart.scales.y;
                    const midpoint = scale.min + (scale.max - scale.min) / 2;

                    return value > midpoint ? 'top' : 'bottom';
                },
                callbacks: {
                    title: (context: any) => {
                        const index = context[0]?.dataIndex;
                        return this.tooltipLabels[index];
                    },
                    label: (context: any) => {
                        const label = context.dataset.label;
                        const cycleTime = context.raw;
                        return label + ': ' + this.formatHoursToDayHour(cycleTime);
                    },
                    labelColor: (context: any) => {
                        const color = context.dataset.borderColor;
                        return {
                            borderColor: color,
                            backgroundColor: color
                        };
                    }
                }
            }
        },
        elements: {
            point: {
                pointStyle: 'circle',
                radius: (context: any) => {
                    const value = context.raw;
                    return value === 0 ? 0 : 4;
                },
                borderColor: (context: any) => {
                    return context.dataset.borderColor;
                },
            }
        },
        scales: {
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12
                    }
                }
            },
            y: {
                position: 'right',
                ticks: {
                    font: {
                        size: 12
                    },
                    precision: 0,
                    callback: (value: number) => {
                        if (value === 0) return '0';
                        return value + (value === 1 ? ' day' : ' days');
                    } 
                },
                border: {
                    display: false
                }
            }
        },
        onHover: (event: any, elements: any, chart: any) => {
            chart.data.datasets.forEach((dataset: any) => {
                if (!dataset.originalColor) {
                    dataset.originalColor = dataset.borderColor;
                }
            });

            if (elements && elements.length > 0) {
                const hoveredIndex = elements[0].datasetIndex;

                chart.data.datasets.forEach((dataset: any, index: number) => {
                    if (index === hoveredIndex) {
                        dataset.borderColor = dataset.originalColor;
                        dataset.borderWidth = 4;
                    } else {
                        dataset.borderColor = this.addOpacity(dataset.originalColor, 0.2);
                        dataset.borderWidth = 2;
                    }
                });
            } else {
                chart.data.datasets.forEach((dataset: any) => {
                    dataset.borderColor = dataset.originalColor || dataset.borderColor;
                    dataset.borderWidth = 2;
                });
            }

            chart.update('none');
        },
        interaction: {
            mode: 'dataset',
            intersect: false
        }
    };

    constructor() { }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges['queueCycleTimes'] && this.queueCycleTimes) {
            this.updateChartData();
        }
    }

    updateChartData(): void {
        const monthBuckets = this.getLastSixMonthsBuckets();

        const queueMap = new Map<string, { name: string; data: (number | null)[] }>();

        for (const entry of this.queueCycleTimes) {
            if (!queueMap.has(entry.queueId)) {
                queueMap.set(entry.queueId, {
                    name: entry.queueName,
                    data: new Array(6).fill(0)
                });
            }

            const bucketIndex = monthBuckets.findIndex(
                b => b.year === entry.exitYear && b.month === entry.exitMonth
            );

            if (bucketIndex !== -1) {
                queueMap.get(entry.queueId)!.data[bucketIndex] = entry.avgQueueCycleTimeInHours / 24;
            }
        }

        const datasets = Array.from(queueMap.entries()).map(([queueId, queue], index) => {
            const color = CHART_COLORS[index % CHART_COLORS.length];
            return {
                queueId,
                label: queue.name,
                data: queue.data,
                borderColor: color,
                backgroundColor: color,
                pointBackgroundColor: 'transparent',
                pointHoverBackgroundColor: 'transparent',
                borderWidth: 2,
                tension: 0.5,
                fill: false
            };
        });

        const buckets = this.getLastSixMonthsBuckets();
        this.tooltipLabels = buckets.map(b => b.fullLabel);

        this.chartData = {
            labels: buckets.map(b => b.label),
            datasets
        };
    }

    getLastSixMonthsBuckets(): { year: number; month: number; label: string; fullLabel: string }[] {
        const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
        const now = new Date();
        return Array.from({ length: 6 }, (_, i) => {
            const d = new Date(now.getFullYear(), now.getMonth() - 5 + i, 1);
            return {
                year: d.getFullYear(),
                month: d.getMonth() + 1,
                label: months[d.getMonth()],
                fullLabel: `${months[d.getMonth()]} ${d.getFullYear()}`
            };
        });
    }

    formatHoursToDayHour(value: number): string {
        if(value === 0) return '0';
        let label = '';
        const hours = Math.ceil(value * 24);
        const days = Math.floor(hours / 24);
        if (days > 0) {
            label += days + (days === 1 ? ' day' : ' days');
        }
        const remainingHours = hours % 24;
        if (remainingHours > 0) {
            if (label) label += ' ';
            label += remainingHours + (remainingHours === 1 ? ' hour' : ' hours');
        }
        return label;
    }

    addOpacity(hex: string, opacity: number): string {
        const r = Number.parseInt(hex.slice(1, 3), 16);
        const g = Number.parseInt(hex.slice(3, 5), 16);
        const b = Number.parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    resetChart() {
        setTimeout(() => {
            const chart = this.chart?.chart;
            if (chart) {
                chart.data.datasets.forEach((dataset: any) => {
                    dataset.borderColor = dataset.originalColor || dataset.borderColor;
                    dataset.borderWidth = 2;
                });
                chart.update('none');
            }
        }, 100);
    }

    toggleDataset(index: number) {
        if (!this.chart?.chart) return;
        const chartInstance = this.chart.chart;
        const isHidden = chartInstance.getDatasetMeta(index).hidden;

        if (isHidden) {
            chartInstance.show(index);
        } else {
            chartInstance.hide(index);
        }
    }

    isDatasetVisible(index: number): boolean {
        if (!this.chart?.chart) return true;
        return !this.chart.chart.getDatasetMeta(index).hidden;
    }

    hasHiddenDatasets(): boolean {
        if (!this.chart?.chart) return false;
        return this.chart.chart.data.datasets.some((_: any, index: number) => !this.isDatasetVisible(index));
    }

    getHiddenQueueIds(): string[] {
        if (!this.chart?.chart) return [];
        return this.chart.chart.data.datasets
            .filter((_: any, index: number) => !this.isDatasetVisible(index))
            .map((d: any) => d.queueId);
    }

    applyHiddenQueueIds(ids: string[]): void {
        if (!this.chart?.chart) return;
        this.chart.chart.data.datasets.forEach((d: any, index: number) => {
            if (ids.includes(d.queueId)) {
                this.chart.chart.hide(index);
            } else {
                this.chart.chart.show(index);
            }
        });
        this.chart.chart.update('none');
    }

    resetAllDatasets(): void {
        if (!this.chart?.chart) return;
        this.chart.chart.data.datasets.forEach((_: any, index: number) => {
            this.chart.chart.show(index);
        });
        this.chart.chart.update();
    }
}
